import { useState, type SyntheticEvent } from 'react';
import { PASSWORD_MIN_LENGTH } from '@indusketch/shared';
import { authApi } from '../../lib/api/endpoints.js';
import { userMessage } from '../../lib/errors.js';
import { useI18n } from '../../i18n/i18n.js';
import { Dialog } from '../../ui/Dialog.js';

/** Own password; other devices are signed out by the API afterwards. */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  const { m, fmt } = useI18n();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (next !== repeat) {
      setError(m.password.mismatch);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await authApi.changePassword({ currentPassword: current, newPassword: next });
      setDone(true);
    } catch (err) {
      setError(userMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={m.password.change} onClose={onClose} testId="change-password">
      {done ? (
        <div className="form-stack">
          <p className="notice" role="status">
            {m.password.changed}
          </p>
          <div className="dialog-actions">
            <button type="button" className="btn btn-primary" onClick={onClose}>
              {m.common.done}
            </button>
          </div>
        </div>
      ) : (
        <form className="form-stack" onSubmit={(e) => void onSubmit(e)}>
          <label className="field">
            <span>{m.password.current}</span>
            <input
              className="input"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              required
              autoComplete="current-password"
            />
          </label>
          <label className="field">
            <span>{fmt(m.password.new, { min: PASSWORD_MIN_LENGTH })}</span>
            <input
              className="input"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              required
              minLength={PASSWORD_MIN_LENGTH}
              autoComplete="new-password"
            />
          </label>
          <label className="field">
            <span>{m.password.repeat}</span>
            <input
              className="input"
              type="password"
              value={repeat}
              onChange={(e) => setRepeat(e.target.value)}
              required
              minLength={PASSWORD_MIN_LENGTH}
              autoComplete="new-password"
            />
          </label>
          {error && (
            <p className="error" role="alert">
              {error}
            </p>
          )}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose}>
              {m.common.cancel}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {m.password.change}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
